import { createAuthorsUrl } from 'helpers/utils';
import { LocationType, SocialMediaType } from 'types/common';
import { ItemType } from 'components/product-cards';

type AuthorSEO = {
  '@type': string
  '@id': string
  name: string
  url: string
  image: string[]
  jobTitle?: string
};

type ArticleSEO = {
  headline: string
  description: string
  image: string
  keywords?: string
  url: string
  datePublished: string
};

type BreadcrumbSEO = {
  position: number
  name: string
  slug: string
};

type OrganizationSEO = {
  url: string
  name: string
  logo: string
  sameAs?: string[]
  email?: string
  telephone?: string
  address?: LocationType
};

type ItemTypeSEO = {
  '@type': string
  position: number
  name: string
};

type ProductSEO = {
  authorName: string
  authorSlug: string
  title: string
  name: string
  description: string
  rating: number
  bestRating: number
  worstRating: number
  image: string
  positiveNotes: ItemTypeSEO[]
  negativeNotes: ItemTypeSEO[]
};

const countWords = (content: string): number => {
  let text = content;
  text = text.replace(/(^\s*)|(\s*$)/gi, '');// exclude  start and end white-space
  text = text.replace(/[ ]{2,}/gi, ' ');// 2 or more space to 1
  text = text.replace(/\n /, '\n'); // exclude newline with a start spacing
  return text.split(' ').filter((str: string) => str !== '').length;
};

const getAbsoluteUrl = (path: string): string => `${process.env.GATSBY_DOMAIN_URL}${path}`;

/* If one social media exists, then we can create the sameAs field */
const hasSocialMedia = (socialMedia: SocialMediaType): boolean => socialMedia && (Object
  .keys(socialMedia) as (keyof SocialMediaType)[])
  .every((key) => !!socialMedia[key]);

/* Filter undefined/null values */
const mapSameAs = (socialMedia: SocialMediaType): string[] => Object
  .values(socialMedia).filter((value) => !!value);

const createPersonJSONLD = (
  name: string,
  url: string,
  socialMedia: SocialMediaType,
  image: string,
  authorOccupation?: string,
): AuthorSEO => ({
  '@type': 'Person',
  '@id': getAbsoluteUrl(url),
  name,
  image: [image],
  ...(authorOccupation) && { jobTitle: authorOccupation },
  url: getAbsoluteUrl(url),
  ...(hasSocialMedia(socialMedia)) && {
    sameAs: mapSameAs(socialMedia),
  },
});

/* This is used for OG Site Name, it's similar to what would be the "Brand name" */
const getSiteName = 'Teeth Talk Girl';

/* Optional parameters are added as params so we don't end up with undefined values when using
* the spread operator */
const createArticlesJSONLD = (
  article: ArticleSEO,
  authorName: string,
  authorSlug: string,
  authorSocialMedia: SocialMediaType,
  authorPhoto: string,
  articleBody: string,
  authorOccupation?: string,
  dateModified?: string,
  wordCount?: number,
): string => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  ...article,
  author: createPersonJSONLD(
    authorName,
    createAuthorsUrl(authorSlug),
    authorSocialMedia,
    authorPhoto,
    authorOccupation,
  ),
  /* Removes most of the tags, is not a problem if some tags remain but it's best to remove as
  * many as we can */
  articleBody: articleBody.replace(/<\/?[^>]+(>|$)/g, ''),
  /* These are for old articles that might not have the wordCount */
  ...(wordCount) ? { wordCount } : { wordCount: countWords(articleBody.replace(/<\/?[^>]+(>|$)/g, '')) },
  ...(dateModified) && { dateModified },
});

const createItemTypeJSONLD = (item: ItemType, position: number): ItemTypeSEO => ({
  '@type': 'ListItem',
  name: item.item,
  position,
});

const createProductReviewJSONLD = (productSEO: ProductSEO): string => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: productSEO.name,
  image: [productSEO.image],
  description: productSEO.description,
  review: {
    '@type': 'Review',
    name: productSEO.title,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: productSEO.rating,
      bestRating: productSEO.bestRating,
      worstRating: productSEO.worstRating,
    },
    author: {
      '@type': 'Person',
      '@id': getAbsoluteUrl(productSEO.authorSlug),
      name: productSEO.authorName,
    },
    positiveNotes: {
      '@type': 'ItemList',
      itemListElement: productSEO.positiveNotes,
    },
    negativeNotes: {
      '@type': 'ItemList',
      itemListElement: productSEO.negativeNotes,
    },
  },
});

const createBreadCrumbJSONLD = (breadCrumbs: BreadcrumbSEO[]): string => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadCrumbs.map((breadCrumb) => ({
    '@type': 'ListItem',
    position: breadCrumb.position,
    name: breadCrumb.name,
    item: getAbsoluteUrl(breadCrumb.slug),
  })),
});

const createOrganizationJSONLD = (organizationSchema: OrganizationSEO): string => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  /* id is a unique id that should almost never change, that's why it's the main url */
  '@id': 'https://www.teethtalkgirl.com',
  ...organizationSchema,
});

export {
  createArticlesJSONLD, createBreadCrumbJSONLD, getAbsoluteUrl, getSiteName,
  createOrganizationJSONLD, hasSocialMedia, mapSameAs, createPersonJSONLD,
  createProductReviewJSONLD, createItemTypeJSONLD,
};
export type {
  ArticleSEO, BreadcrumbSEO, OrganizationSEO, ProductSEO, ItemTypeSEO,
};
